import { OptionsItemLiveData, OptionsItemLocData, TextStatesData, TextureStatesData } from "../../../types/types";
import { ApplyMixins } from "../../../utils/ApplyMixins";
import { Interactive } from "../../../mixins/Interactive";
import { Showable } from "../../../mixins/Showable";
import { UpdateView } from "../../../mixins/UpdateView";
import { ISize } from "../../../controls/scrollpane/ISize";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { RectSize } from "../../../utils/RectSize";
import { ButtonBase, ButtonEvents } from "../../../ui/ButtonBase";
import { OptionsItemBase } from "./OptionsItemBase";
import isMobile from 'ismobilejs';


// get ts linting
export interface OptionsSelectorItem extends Showable, Interactive, UpdateView { }

/**
 * @alike: ComboBox
 * Item that triggers container with list of items to select from
 * - the items might be list of text items, or list of thumbs, 
 * but each item from selection will also return a selectedIndex by which a 
 * secondary label for the selector can be retrieved.
 */
export class OptionsSelectorItem
    extends OptionsItemBase
    implements ISize {
    public btnHelp: ButtonBase;
    public selectorLabel: Phaser.GameObjects.Text;
    public selectorArrow: Phaser.GameObjects.Image;

    private static appliedMixins: boolean = false;

    // callbacks back to parent for resolution
    // protected onItemSelectorChange: Function; // not usable here, moved to popup
    protected onItemSelectorClick: Function;
    protected onItemHelpClick: Function;
    // protected selectedItemSuffix: string = " >";
    protected selectorArrowSpacing = -20;
    protected selectorArrowPaddingRight = 15;


    public Init(
        // itemIndex: number,
        bgTextureStates: TextureStatesData,
        labelTextStates: TextStatesData,
        selectorLabelTextStates: TextStatesData,
        onItemSelectorClick: Function,
        onItemHelpClick: Function,
        delimiterLineAlpha: number,
    ): void {
        if (!OptionsSelectorItem.appliedMixins) {
            ApplyMixins(OptionsSelectorItem, [Showable, Interactive, UpdateView]);
            OptionsSelectorItem.appliedMixins = true;
        }

        // this.index = itemIndex;
        this.onItemSelectorClick = onItemSelectorClick;
        this.onItemHelpClick = onItemHelpClick;
        this.delimiterLineAlpha = delimiterLineAlpha;

        // use this when deciding exact item size @see parent->OnResize
        // this.type = "OptionsSelectorItem";
        this.updateViews = [];
        this.size = new RectSize();
        // set default (mixin) props
        // NOTE: default values set during mixin declaration are undefined)
        // Showable
        this.isShowing = false;
        // Interactive
        // this.isInteractive = undefined;
        // UpdateView
        this.pointerOver = false;

        const _isMobile: boolean = isMobile(window.navigator).any;

        this.bg = this.scene.make.image({ 
            x: 0, y: 0, 
            key: bgTextureStates.out.texture, 
            frame: bgTextureStates.out.frame 
        });
        this.bg.name = "OptionsSelectorItem_bg";
        this.bg.setOrigin(0);
        this.add(this.bg);

        this.imgDelimiterLine = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "rect-2x2-white.png" });
        this.imgDelimiterLine.setOrigin(0, 0.5);
        this.imgDelimiterLine.alpha = this.delimiterLineAlpha;
        this.add(this.imgDelimiterLine);

        this.btnHelp = new ButtonBase(this.scene);
        this.btnHelp.InitBase(
            _isMobile ? {
                out: { texture: LoaderFileKeys.ui, frame: "btn-options-help-out.png" },
                // over: { texture: LoaderFileKeys.ui, frame: "btn-options-help-over.png" }
            } : {
                out: { texture: LoaderFileKeys.ui, frame: "btn-options-help-out.png" },
                over: { texture: LoaderFileKeys.ui, frame: "btn-options-help-over.png" }
            }
        );
        this.btnHelp.PostInit(30);
        this.add(this.btnHelp);

        this.label = this.scene.make.text({
            // text: label, 
            style: labelTextStates.out,
        });
        this.label.setAlign("left");
        this.label.setOrigin(0, 0.5);
        this.add(this.label);

        this.selectorLabel = this.scene.make.text({
            style: selectorLabelTextStates.out,
        });
        this.selectorLabel.setAlign("right");
        this.selectorLabel.setOrigin(1, 0.5);
        this.add(this.selectorLabel);

        this.selectorArrow = this.scene.make.image({
            key: LoaderFileKeys.ui,
            frame: "scrollArrow-right-out.png",
            scale: 0.8,
        });
        this.selectorArrow.setOrigin(0.5);
        this.add(this.selectorArrow);

        if (!_isMobile) {
            this.AddViewDef({
                view: this.bg,
                textureStates: bgTextureStates,
            });

            this.AddViewDef({
                view: this.label,
                textStates: labelTextStates,
            });

            this.AddViewDef({
                view: this.selectorLabel,
                textStates: selectorLabelTextStates,
            });

            this.AddViewDef({
                view: this.selectorArrow,
                textureStates: {
                    out: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-out.png" },
                    over: { texture: LoaderFileKeys.ui, frame: "scrollArrow-right-over.png" },
                },
            });
        }
        this.SetHitAreaFromViews([this.bg], 0);
    }

    public override RegisterListeners(): void {
        // console.log("RegisterListeners");

        // super.RegisterListeners(); // trace all at once (prettier output)
        this.AddListeners();

        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnClick, this);
        this.btnHelp.on(ButtonEvents.CLICK, this.OnBtnHelpClick, this);
    }

    public override UnregisterListeners(): void {
        // console.log("UnregisterListeners");

        // super.UnregisterListeners(); // trace all at once (prettier output)
        this.RemoveListeners();

        this.off(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnClick);
        this.btnHelp.off(ButtonEvents.CLICK, this.OnBtnHelpClick);
    }

    /**
     * Bg was clicked. 
     * This registers as item click. 
     * Open 'choices' popup for this item.
     */
    protected OnClick(): void {
        // call external callback (if exists)
        this.onItemSelectorClick?.(
            // this.index,
            this,
            this.itemLayoutData,
            this.itemThemeData,
            this.itemLocData,
            this.itemLiveData,
        );
    }

    protected OnBtnHelpClick(): void {
        // dispatch change
        if (this.onItemHelpClick != undefined) {
            this.onItemHelpClick(
                this.itemLayoutData,
                this.itemThemeData,
                this.itemLocData,
                this.itemLiveData,
            );
        }
    }

    /**
     * Data model(s) changed. Update view.
     * 
     * Implicit (@see SetData) or explicit (this) call to update from either:
     * 1) localization (texts changed) or 
     * 2) live data change (value indexes changed)
     * 
     * NOTE: The 'Data' in 'DataChange' means any data, eg live data, 
     *       localization data or any other data this component needs to update its view.
     * NOTE: its possible to call this function without arguments, 
     *       which will refresh view from currently stored live data (if available)
     *       - this is advantageous for situations where other parts of app 
     *         change same data by reference, to refresh here from.
     */
    public override UpdateViewFromDataChange(
        itemLocData: OptionsItemLocData = undefined,
        itemLiveData: OptionsItemLiveData = undefined
    ): void {
        this.imgDelimiterLine.visible = this.hasDelimiterLine;

        // prefer left, pick right if left is undefined.
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Nullish_coalescing_operator
        const bestLocData: OptionsItemLocData = itemLocData ?? this.itemLocData;
        const bestLiveData: OptionsItemLiveData = itemLiveData ?? this.itemLiveData;
        if (bestLocData) {
            if (bestLiveData) {
                this.label.text = bestLocData.itemLabel;
                // this.selectorLabel.text = bestLocData.choices[bestLiveData.value as number].itemLabel as string + this.selectedItemSuffix;
                this.selectorLabel.text = bestLocData.choices[bestLiveData.value as number].itemLabel as string;
            }
            const hasHelp = bestLocData.help == undefined ? false : true;
            this.btnHelp.visible = hasHelp;
        }
    }

    /**
     * @alike: OnResize
     * Uses 'maximum allowed width' to compute item's resulting height
     * - Item's width and height depends on height of label textfield
     * - label's height depends on amount of HORIZONTAL space and maximum horizontal space allowed for item
     * 
     * @param width 
     */
    public override SetWidthGetHeight(width: number): number {
        // total size incl extra space that the help btn would take..
        const btnHelpWidthInclSpacing = this.btnHelp.visible ? this.btnHelp.displayWidth + this.itemSpacing.width : 0;
        const btnHelpHeight = this.btnHelp.visible ? this.btnHelp.displayHeight : 0;

        this.label.style.setWordWrapWidth(width - 2 * this.borderPadding.horizontal - this.itemSpacing.width - btnHelpWidthInclSpacing - this.selectorArrowSpacing - this.selectorArrowPaddingRight - this.selectorLabel.displayWidth - this.selectorArrow.displayWidth);

        const height = Math.max(
            this.label.displayHeight + this.borderPadding.top + this.borderPadding.bottom,
            this.selectorLabel.displayHeight + this.borderPadding.top + this.borderPadding.bottom,
            btnHelpHeight + this.borderPadding.top + this.borderPadding.bottom
        );
        // console.log(maxHeight);

        // computes and returns height
        return height;
    }

    public OnResize(): void {
        const centerY = this.size.height / 2;

        this.bg.setDisplaySize(this.size.width, this.size.height);

        // delimiter (if item has it -- last item in list does not have it)
        if (this.hasDelimiterLine) {
            // origin 0,1 (bot left)
            this.imgDelimiterLine.y = this.size.height;
            this.imgDelimiterLine.displayWidth = this.size.width;
        }

        // label origin (0, 0.5)
        this.label.setPosition(this.borderPadding.horizontal, centerY);
        // 
        if (this.btnHelp.visible) {
            this.btnHelp.setPosition(this.label.x + this.label.displayWidth + this.itemSpacing.width + this.btnHelp.displayWidth / 2, centerY);
            // this.btnHelp.x = this.label.x + this.label.displayWidth + this.itemSpacing.width + this.btnHelp.displayWidth/2;
        }
        // toggleIcon origin (1, 0.5)
        this.selectorLabel.setPosition(
            this.size.width - this.borderPadding.horizontal - this.selectorArrowSpacing - this.selectorArrowPaddingRight - this.selectorArrow.displayWidth, 
            centerY,
        );
        this.selectorArrow.setPosition(
            this.size.width - this.borderPadding.horizontal - this.selectorArrowPaddingRight, 
            centerY,
        );
        // 
        this.SetHitAreaRectangle(new Phaser.Geom.Rectangle(0, 0, this.size.width, this.size.height));
    }

    // public override UpdateFromLocalizationChange(): void
    // {
    //     // console.log("UpdateFromLocalizationChange");

    //     const centerY = this.size.height/2;
    //     if (this.btnHelp.visible) {
    //         this.btnHelp.setPosition(this.label.x + this.label.displayWidth + this.itemSpacing.width + this.btnHelp.displayWidth/2, centerY);
    //         // this.btnHelp.x = this.label.x + this.label.displayWidth + this.itemSpacing.width + this.btnHelp.displayWidth/2;
    //     }
    // }
}